import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ArrowDownRight, RotateCcw, Plus, CheckCircle2, AlertTriangle, FileText } from 'lucide-react';
import type { LedgerEntry } from '../../../../../shared/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost/fhpf-api';

export const ExpenseManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [showAddModal, setShowAddModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Legal & Documentation');
  const [description, setDescription] = useState('');
  const [voucherRef, setVoucherRef] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reversal State
  const [reversingEntry, setReversingEntry] = useState<LedgerEntry | null>(null);
  const [reversalReason, setReversalReason] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'ledger'),
      where('type', 'in', ['expense', 'reversal']),
      orderBy('sequenceNumber', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: LedgerEntry[] = [];
        snap.forEach((doc) => list.push(doc.data() as LedgerEntry));
        setExpenses(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Expenses load error:', err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const numAmount = parseInt(amount, 10);
    if (!numAmount || numAmount <= 0) {
      alert('Amount must be a positive integer in BDT.');
      return;
    }

    if (description.trim().length < 5) {
      alert('Description must be at least 5 characters.');
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/expenses`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: numAmount,
          category,
          description: description.trim(),
          voucherRef: voucherRef.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Failed to record expense.');
      }

      setMessage({ type: 'success', text: `Expense recorded in Ledger sequence #${data.sequenceNumber}.` });
      setShowAddModal(false);
      setAmount('');
      setDescription('');
      setVoucherRef('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to record expense.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReverseExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !reversingEntry) return;

    if (reversalReason.trim().length < 5) {
      alert('Reversal reason must be at least 5 characters.');
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/expenses/${reversingEntry.entryId}/reverse`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: reversalReason.trim() }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Failed to reverse expense.');
      }

      setMessage({
        type: 'success',
        text: `Expense reversed with compensating entry at Ledger sequence #${data.reversalSequenceNumber}.`,
      });
      setReversingEntry(null);
      setReversalReason('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to reverse expense.' });
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;

  return (
    <div className="space-y-6">
      {/* Top action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ArrowDownRight className="w-5 h-5 text-amber-500" />
            <span>Group Operational Expenses</span>
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Recorded expenses are deducted from group funds and permanently logged in the ledger.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold flex items-center gap-2 shadow-sm shadow-primary-600/20"
        >
          <Plus className="w-4 h-4" />
          <span>{t('admin.addExpense')}</span>
        </button>
      </div>

      {/* Notifications */}
      {message && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-2.5 text-xs sm:text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
              : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Expenses Table */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : expenses.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">No expenses recorded yet.</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-navy-800">
            {expenses.map((e) => (
              <div key={e.entryId} className="p-4 sm:p-5 flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-gray-500">#{e.sequenceNumber}</span>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        e.type === 'reversal'
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                      }`}
                    >
                      {e.type.toUpperCase()}
                    </span>
                    <span className="font-semibold text-sm text-gray-900 dark:text-white">{e.note}</span>
                  </div>

                  <div className="text-xs text-gray-400 flex items-center gap-3">
                    <span>{e.createdAt}</span>
                    <span>Ref: {e.refId}</span>
                    <span className="font-mono text-[10px]">Hash: {e.entryHash.substring(0, 10)}...</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="font-bold text-base text-gray-900 dark:text-white">
                      {fmt(e.amount)}
                    </span>
                  </div>

                  {e.type === 'expense' && (
                    <button
                      type="button"
                      onClick={() => setReversingEntry(e)}
                      className="p-2 rounded-xl border border-gray-200 dark:border-navy-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-800 text-xs font-medium flex items-center gap-1"
                      title="Reverse this expense"
                    >
                      <RotateCcw className="w-3.5 h-3.5 text-red-500" />
                      <span className="hidden sm:inline">Reverse</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Expense Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-navy-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-navy-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Record Operational Expense
            </h3>

            <form onSubmit={handleAddExpense} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Amount (BDT)
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 5000"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                >
                  <option value="Legal & Documentation">Legal & Documentation</option>
                  <option value="Land Survey & Soil Test">Land Survey & Soil Test</option>
                  <option value="Audit & Accounting">Audit & Accounting</option>
                  <option value="Meeting & Administrative">Meeting & Administrative</option>
                  <option value="Bank Charges">Bank Charges</option>
                  <option value="Other Operational">Other Operational</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  required
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Detail the expense purpose and approval..."
                  className="w-full p-2.5 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Voucher / Receipt Reference #
                </label>
                <input
                  type="text"
                  value={voucherRef}
                  onChange={(e) => setVoucherRef(e.target.value)}
                  placeholder="e.g. VOUCHER-2026-001"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 dark:border-navy-700 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary-600 hover:bg-primary-700 text-white"
                >
                  Confirm Expense
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reversal Modal */}
      {reversingEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-navy-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-navy-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Reverse Expense: {reversingEntry.entryId}
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Reversing this entry ({fmt(reversingEntry.amount)}) will create a compensating reversal ledger entry restoring group balances. The original entry remains in historical records.
            </p>

            <form onSubmit={handleReverseExpense} className="space-y-4">
              <textarea
                required
                rows={3}
                value={reversalReason}
                onChange={(e) => setReversalReason(e.target.value)}
                placeholder="State reason for reversal (e.g. Duplicate entry, refunded)..."
                className="w-full p-3 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-white"
              />

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReversingEntry(null)}
                  className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 dark:border-navy-700 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold rounded-xl bg-red-600 hover:bg-red-700 text-white"
                >
                  Confirm Reversal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
